/**
 * ========================================
 * SHARED API CLIENT FOR ALL SIGNUP FORMS
 * ========================================
 * 
 * This is the SINGLE source of truth for:
 * 1. Fetching geolocation data (country, IP, city, state)
 * 2. Submitting leads to CRM
 * 
 * All forms MUST use this client - no duplicate API calls.
 */

// ========================================
// GEOLOCATION API
// ========================================

/**
 * Fetch user's geolocation data
 * 
 * Endpoint: GET https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation
 * 
 * @returns {Promise<Object|null>} Geolocation response or null if failed
 * 
 * Response structure:
 * {
 *   isSuccess: true,
 *   data: {
 *     ipAddress: "84.17.47.123",
 *     countryCode: "NL",
 *     countryName: "Netherlands",
 *     regionName: "North Holland",
 *     cityName: "Amsterdam",
 *     latitude: "52.37",
 *     longitude: "4.89"
 *   },
 *   errorMessage: "",
 *   statusCode: 200
 * }
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch('https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation', {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('GeoLocation API error:', error);
    return null;
  }
};

// Legacy alias for backward compatibility
export const fetchGeoLocation = getGeoLocation;

// ========================================
// LEAD SUBMISSION API
// ========================================

/**
 * Submit lead to CRM
 * 
 * Endpoint: POST /api/Lead/AddLead
 * 
 * @param {Object} payload - Complete lead payload (see buildLeadPayload helper)
 * @returns {Promise<Object>} Standardized response
 * 
 * Response structure:
 * {
 *   success: true/false,
 *   statusCode: 200|400|401|500,
 *   customerId: "abc123" (only on 200),
 *   message: "Error message if any"
 * }
 * 
 * Status code handling:
 * - 200: Success → redirect to /application/main?customerid={customerId}
 * - 401: Unauthorized → show response.message
 * - 400: Bad Request → show response.errorMessage
 */
export const submitLead = async (payload) => {
  try {
    const response = await fetch('/api/Lead/AddLead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const responseData = await response.json();

    // 200 Success - has customerId
    if (response.status === 200 && responseData.isSuccess && responseData.data?.customerId) {
      return {
        success: true,
        statusCode: 200,
        customerId: responseData.data.customerId,
        message: responseData.data?.message || 'Success'
      };
    }
    
    // 401 Unauthorized - use message field
    if (response.status === 401) {
      return {
        success: false,
        statusCode: 401,
        message: responseData.message || 'Unauthorized access'
      };
    }
    
    // 400 Bad Request - use errorMessage field
    if (response.status === 400) {
      return {
        success: false,
        statusCode: 400,
        message: responseData.errorMessage || 'Invalid data provided'
      };
    }

    // Other errors
    return {
      success: false,
      statusCode: response.status,
      message: responseData.errorMessage || responseData.message || 'Something went wrong. Please try again.'
    };
    
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      statusCode: 500,
      message: 'Network error. Please check your connection and try again.'
    };
  }
};

// ========================================
// PAYLOAD BUILDER
// ========================================

/**
 * Build Lead API payload with correct field mapping
 * 
 * REQUIRED FIELDS:
 * - name, email, countryDialCode, country, phoneNumber, ipAddress, description, pageUrl
 * 
 * OPTIONAL FIELDS:
 * - city, state, geoLocation, packageTitle, packageAmount
 * 
 * RULES:
 * 1. phoneNumber must be NUMERIC ONLY (no +, spaces, or special chars)
 * 2. countryDialCode is a separate NUMERIC field (e.g., 31, not "+31")
 * 3. country must be countryCode from geo (e.g., "NL", not "Netherlands")
 * 4. ipAddress from geoLocation data
 * 5. city/state from geo if available
 * 6. geoLocation is stringified JSON
 * 
 * @param {Object} formData - Form values from user
 * @param {Object} geoData - GeoLocation API response
 * @param {Object} options - Optional fields (packageTitle, packageAmount, pageUrl)
 * @returns {Object} Properly formatted payload for Lead API
 * 
 * Example usage:
 * const payload = buildLeadPayload({
 *   name: "John Doe",
 *   email: "john@example.com",
 *   phoneNumber: "777888999",
 *   countryDialCode: 31,
 *   description: "Need help with trademark"
 * }, geoData, {
 *   packageTitle: "Standard",
 *   packageAmount: 149
 * });
 */
export const buildLeadPayload = (formData, geoData = null, options = {}) => {
  // Clean phone number - NUMERIC ONLY
  const cleanPhone = (formData.phoneNumber || '').replace(/\D/g, '');
  
  // Build base payload with REQUIRED fields
  const payload = {
    name: formData.name,
    email: formData.email,
    countryDialCode: parseInt(formData.countryDialCode) || 1,
    country: geoData?.data?.countryCode || formData.country || 'US',
    phoneNumber: cleanPhone,
    ipAddress: geoData?.data?.ipAddress || '',
    description: formData.description || '',
    pageUrl: options.pageUrl || window.location.href
  };

  // Add OPTIONAL fields only if they exist (no empty strings)
  if (geoData?.data?.cityName) {
    payload.city = geoData.data.cityName;
  }
  
  if (geoData?.data?.regionName) {
    payload.state = geoData.data.regionName;
  }
  
  if (geoData) {
    payload.geoLocation = JSON.stringify(geoData);
  }
  
  if (options.packageTitle) {
    payload.packageTitle = options.packageTitle;
  }
  
  if (options.packageAmount !== undefined && options.packageAmount !== null) {
    payload.packageAmount = Number(options.packageAmount) || 0;
  }

  return payload;
};

/**
 * Get country data from geolocation
 * @param {Object} geoData - Geolocation response
 * @returns {Object} Country information with code, name, and dialCode
 */
export const getCountryFromGeo = (geoData) => {
  if (!geoData?.data) {
    return {
      code: 'US',
      name: 'United States',
      dialCode: '1'
    };
  }

  return {
    code: geoData.data.countryCode,
    name: geoData.data.countryName,
    dialCode: getDialCodeFromCountry(geoData.data.countryCode)
  };
};

/**
 * Get dial code from country code
 * @param {string} countryCode - ISO country code
 * @returns {string} Dial code
 */
const getDialCodeFromCountry = (countryCode) => {
  const dialCodes = {
    'US': '1',
    'CA': '1',
    'GB': '44',
    'AU': '61',
    'IN': '91',
    'PK': '92',
    'BD': '880',
    'CN': '86',
    'JP': '81',
    'DE': '49',
    'FR': '33',
    'IT': '39',
    'ES': '34',
    'NL': '31',
    'BE': '32',
    'SE': '46',
    'NO': '47',
    'DK': '45',
    'FI': '358',
    'PL': '48',
    'RU': '7',
    'BR': '55',
    'MX': '52',
    'AR': '54',
    'CL': '56',
    'CO': '57',
    'PE': '51',
    'VE': '58',
    'ZA': '27',
    'EG': '20',
    'NG': '234',
    'KE': '254',
    'GH': '233',
    'SA': '966',
    'AE': '971',
    'IL': '972',
    'TR': '90',
    'GR': '30',
    'PT': '351',
    'IE': '353',
    'NZ': '64',
    'SG': '65',
    'MY': '60',
    'TH': '66',
    'PH': '63',
    'ID': '62',
    'VN': '84',
    'KR': '82',
    'HK': '852',
    'TW': '886'
  };

  return dialCodes[countryCode] || '1';
};

/**
 * Get phone number length for a country
 * @param {string} format - Phone format string (e.g., "(###) ###-####")
 * @returns {number} Expected length of phone number digits
 */
export const getPhoneLengthFromFormat = (format) => {
  if (!format) return 10; // Default to 10 digits
  // Count # characters in format
  return (format.match(/#/g) || []).length;
};

/**
 * Validate phone number for a specific country
 * @param {string} phoneNumber - Phone number to validate
 * @param {Object} country - Country object with format
 * @returns {Object} Validation result with isValid boolean and message
 */
export const validatePhoneNumber = (phoneNumber, country) => {
  // Extract only digits
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  if (!digitsOnly) {
    return {
      isValid: false,
      message: 'Phone number is required'
    };
  }

  // Get expected length from format
  const expectedLength = country?.format ? getPhoneLengthFromFormat(country.format) : 10;
  
  if (digitsOnly.length < expectedLength) {
    return {
      isValid: false,
      message: `Phone number should be ${expectedLength} digits for ${country?.name || 'selected country'}`
    };
  }

  if (digitsOnly.length > expectedLength) {
    return {
      isValid: false,
      message: `Phone number should be ${expectedLength} digits for ${country?.name || 'selected country'}`
    };
  }

  return {
    isValid: true,
    message: ''
  };
};

// ========================================
// COMPLETE EXAMPLE USAGE
// ========================================

/**
 * Example: Complete form submission flow
 * 
 * This shows how to use the API client in any signup form:
 * 
 * ```javascript
 * import { getGeoLocation, buildLeadPayload, submitLead } from './services/api';
 * 
 * async function handleFormSubmit(e) {
 *   e.preventDefault();
 *   
 *   // 1. Get geolocation (usually done on form load)
 *   const geo = await getGeoLocation();
 *   
 *   // 2. Display country on form
 *   if (geo?.data?.countryCode) {
 *     displayCountry(geo.data.countryCode);
 *   }
 *   
 *   // 3. Build payload with proper field mapping
 *   const payload = buildLeadPayload({
 *     name: formValues.name,
 *     email: formValues.email,
 *     phoneNumber: formValues.phone, // Will be cleaned to numeric only
 *     countryDialCode: formValues.dialCode, // e.g., 31
 *     description: formValues.description
 *   }, geo, {
 *     packageTitle: "Standard",
 *     packageAmount: 149
 *   });
 *   
 *   // 4. Submit to Lead API
 *   const result = await submitLead(payload);
 *   
 *   // 5. Handle response
 *   if (result.statusCode === 200 && result.customerId) {
 *     // Success - redirect to application
 *     window.location.href = `/application/main?customerid=${result.customerId}`;
 *   } else {
 *     // Error - show message
 *     showError(result.message);
 *   }
 * }
 * ```
 * 
 * ✅ CORRECT PAYLOAD EXAMPLE:
 * {
 *   "name": "Syed Fawad Hussain",
 *   "email": "sfawadh@gmail.com",
 *   "countryDialCode": 31,
 *   "country": "NL",
 *   "phoneNumber": "777888999",
 *   "ipAddress": "84.17.47.123",
 *   "description": "Need trademark registration service",
 *   "pageUrl": "https://example.com/",
 *   "city": "Amsterdam",
 *   "state": "North Holland",
 *   "geoLocation": "{...}",
 *   "packageTitle": "Standard",
 *   "packageAmount": 149
 * }
 * 
 * ❌ INCORRECT PAYLOAD:
 * {
 *   "phoneNumber": "+31 777888999",  // Wrong: has symbols
 *   "country": "Netherlands"          // Wrong: use countryCode "NL"
 * }
 */
